import { useRouterState } from "@tanstack/react-router";
import { WifiOff } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { ConnectionStatusBannerSurface } from "@/components/connection-status-banner-surface";
import { Spinner } from "@/components/ui/spinner";
import { type NetworkStatus, useNetworkStatus } from "@/hooks";
import { trackNetworkStatusChange } from "@/lib/observability";

/**
 * Banner configuration for each network status.
 */
const BANNER_CONFIG: Record<
  Exclude<NetworkStatus, "loading" | "connected">,
  {
    icon: React.ReactNode;
    message: string;
    submessage: string;
    tone: "offline" | "reconnecting";
  }
> = {
  offline: {
    icon: <WifiOff className="size-4" />,
    message: "You're offline",
    submessage: "Navigation and changes are disabled until you reconnect",
    tone: "offline",
  },
  reconnecting: {
    icon: <Spinner className="size-4" />,
    message: "Reconnecting to server...",
    submessage: "Some features may be unavailable",
    tone: "reconnecting",
  },
};

const RECONNECT_BANNER_DELAY_MS = 1000;

/**
 * NetworkStatusBanner Component
 *
 * Displays a persistent banner when the app loses connectivity:
 * - Amber banner: Browser is offline (no network)
 * - Blue banner: Browser online but Convex WebSocket disconnected
 *
 * Features:
 * - Auto-shows when connection is lost
 * - Auto-dismisses when connection restores
 * - Route-aware: Hidden on auth and error routes
 * - Shows retry count for extended reconnection attempts
 *
 * @example
 * ```tsx
 * // In __root.tsx RootLayout
 * return (
 *   <>
 *     <NetworkStatusBanner />
 *     {/* rest of layout *\/}
 *   </>
 * );
 * ```
 */
export function NetworkStatusBanner() {
  const { status, connectionRetries } = useNetworkStatus();
  const [showReconnectBanner, setShowReconnectBanner] = useState(false);

  // Get pathname from router state (requires being inside RouterProvider)
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // Track previous status for observability
  const previousStatusRef = useRef<NetworkStatus | null>(null);

  // Track network status changes for debugging
  useEffect(() => {
    trackNetworkStatusChange(status, previousStatusRef.current, connectionRetries);
    previousStatusRef.current = status;
  }, [status, connectionRetries]);

  useEffect(() => {
    if (status !== "reconnecting") {
      setShowReconnectBanner(false);
      return;
    }

    const timeoutId = setTimeout(() => {
      setShowReconnectBanner(true);
    }, RECONNECT_BANNER_DELAY_MS);

    return () => clearTimeout(timeoutId);
  }, [status]);

  // Don't show banner on auth or error routes
  const isAuthRoute = pathname.startsWith("/auth");
  const isErrorRoute = pathname.startsWith("/error");

  if (isAuthRoute || isErrorRoute) {
    return null;
  }

  const displayStatus =
    status === "offline"
      ? "offline"
      : status === "reconnecting" && showReconnectBanner
        ? "reconnecting"
        : null;

  if (displayStatus === null) {
    return null;
  }

  const config = BANNER_CONFIG[displayStatus];

  return (
    <ConnectionStatusBannerSurface
      icon={config.icon}
      title={config.message}
      submessage={config.submessage}
      tone={config.tone}
      retryCount={displayStatus === "reconnecting" ? connectionRetries : undefined}
    />
  );
}
